import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Sparkles,
  X,
  Send,
  Loader2,
  RotateCcw,
} from "lucide-react";

const STORAGE_KEY = "upcel_ai_session";

export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(
    () => localStorage.getItem(STORAGE_KEY) || ""
  );
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && sessionId && messages.length === 0) {
      api
        .get(`/ai/sessions/${sessionId}`)
        .then(({ data }) => setMessages(data || []))
        .catch(() => {});
    }
  }, [open, sessionId, messages.length]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const send = async () => {
    const txt = input.trim();
    if (!txt || busy) return;
    setMessages((m) => [
      ...m,
      { role: "user", content: txt, created_at: new Date().toISOString() },
    ]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", {
        session_id: sessionId || undefined,
        message: txt,
      });
      if (data.session_id) {
        setSessionId(data.session_id);
        localStorage.setItem(STORAGE_KEY, data.session_id);
      }
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: data.reply,
          created_at: new Date().toISOString(),
        },
      ]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro na IA");
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: "Ops, tive um problema para responder. Tenta de novo? 🙏",
          created_at: new Date().toISOString(),
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const newChat = () => {
    setSessionId("");
    setMessages([]);
    localStorage.removeItem(STORAGE_KEY);
  };

  const suggestions = [
    "Como está o estoque hoje?",
    "Quantas OS estão em aberto?",
    "Lucro do mês",
    "Escreve uma mensagem pro cliente avisando que a OS está pronta",
  ];

  const renderContent = (t) => {
    // Simple markdown: **bold**, ```code```, links
    const parts = t.split(/(```[\s\S]*?```|\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith("```") && part.endsWith("```")) {
        const code = part.slice(3, -3).replace(/^\w*\n/, "");
        return (
          <pre
            key={i}
            className="bg-slate-900 text-slate-100 text-xs rounded-sm p-2 my-1 whitespace-pre-wrap overflow-x-auto"
          >
            {code}
          </pre>
        );
      }
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={i}>{part.slice(2, -2)}</strong>
        );
      }
      return <span key={i}>{part}</span>;
    });
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="ai-open-btn"
          className="fixed bottom-20 right-6 z-40 bg-gradient-to-br from-[#2563EB] to-[#0A1628] text-white rounded-full w-14 h-14 flex items-center justify-center shadow-lg hover:scale-105 transition-transform"
          title="Assistente IA"
          style={{ boxShadow: "0 8px 24px rgba(37,99,235,0.35)" }}
        >
          <Sparkles size={22} />
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-20 right-6 z-40 w-[380px] max-w-[calc(100vw-48px)] h-[560px] max-h-[calc(100vh-100px)] bg-white border border-slate-200 rounded-sm shadow-2xl flex flex-col overflow-hidden"
          data-testid="ai-panel"
        >
          {/* Header */}
          <div className="brand-navy text-white p-3 flex items-center gap-2 border-b border-white/10">
            <Sparkles size={16} className="text-blue-300" />
            <div className="flex-1">
              <div className="font-display font-bold text-sm">
                Assistente UP CEL Quilombo
              </div>
              <div className="text-[10px] uppercase tracking-wider text-blue-300">
                Gemini 2.5 Flash
              </div>
            </div>
            <button
              onClick={newChat}
              title="Nova conversa"
              data-testid="ai-new-chat"
              className="p-1.5 hover:bg-white/10 rounded-sm"
            >
              <RotateCcw size={14} />
            </button>
            <button
              onClick={() => setOpen(false)}
              data-testid="ai-close-btn"
              className="p-1.5 hover:bg-white/10 rounded-sm"
            >
              <X size={16} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto scrollbar-thin p-3 space-y-3 bg-slate-50"
          >
            {messages.length === 0 && !busy && (
              <div className="text-center text-xs text-slate-500 py-6 space-y-2">
                <Sparkles
                  size={28}
                  className="mx-auto text-[#2563EB] opacity-70"
                />
                <p>Oi! Sou o assistente da UPCEL.</p>
                <p>Posso te ajudar com dúvidas do sistema, consultar suas vendas, estoque, OS e redigir mensagens para clientes. 👋</p>
                <div className="flex flex-wrap gap-1.5 justify-center mt-3">
                  {suggestions.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => setInput(s)}
                      className="text-[10px] bg-white border border-slate-300 hover:border-[#2563EB] hover:text-[#2563EB] px-2 py-1 rounded-sm"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${
                  m.role === "user" ? "justify-end" : "justify-start"
                }`}
                data-testid={`ai-msg-${m.role}`}
              >
                <div
                  className={`max-w-[85%] rounded-sm p-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#2563EB] text-white"
                      : "bg-white border border-slate-200 text-slate-800"
                  }`}
                >
                  {m.role === "user" ? m.content : renderContent(m.content)}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-sm p-2.5 text-sm flex items-center gap-2 text-slate-500">
                  <Loader2 size={14} className="animate-spin" /> pensando…
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-2.5 border-t border-slate-200 bg-white">
            <div className="flex gap-1.5">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                rows={1}
                placeholder="Pergunte alguma coisa…"
                data-testid="ai-input"
                disabled={busy}
                className="flex-1 resize-none px-2.5 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB] max-h-24"
              />
              <button
                onClick={send}
                disabled={busy || !input.trim()}
                data-testid="ai-send-btn"
                className="bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-40 text-white w-9 rounded-sm flex items-center justify-center"
              >
                <Send size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
